import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/gatus')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Gatus Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Gatus: Gatus is a self-hosted health dashboard configured in YAML. Uptrack is hosted, with a web UI and alert confirmation." },
      { property: 'og:title', content: 'Gatus Alternative — Uptrack' },
      { property: 'og:description', content: "Gatus is free, open source, and configured in YAML. Uptrack is hosted, with a web UI, alert confirmation, and team roles." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-gatus.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/gatus' },
    ],
    scripts: comparisonScripts('gatus', "Gatus"),
  }),
})

const data: ComparisonData = {
  competitor: "Gatus",
  tagline: "Gatus is an open-source health dashboard written in Go and configured in YAML, which suits GitOps workflows. Uptrack is a hosted alternative with a web UI, from $3/mo.",
  heroDescription: "These facts come from the Gatus README and GitHub repository as of October 2026, and from Uptrack's pricing page.",

  features: [
    { feature: "Pricing", uptrack: "Free plan (5 monitors); Pro from $3/mo", competitor: "Free and open source (Apache 2.0)" },
    { feature: "Configuration", uptrack: "Web UI, REST API, or CLI", competitor: "YAML files (no web UI for setup)", uptrackWins: true },
    { feature: "Infrastructure required", uptrack: "None (hosted)", competitor: "Your own server or cluster", uptrackWins: true },
    { feature: "Alert confirmation", uptrack: "2-5 consecutive failed checks", competitor: "failure-threshold in YAML (default 3)" },
    { feature: "Check locations", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "Wherever you deploy it" },
    { feature: "Check types", uptrack: "HTTP, keyword, TCP, ping, DNS, SSL, domain expiry, heartbeat, browser (Playwright)", competitor: "HTTP, TCP, ICMP, DNS, WebSocket, gRPC, SSH, STARTTLS, TLS, UDP, SCTP" },
    { feature: "Kubernetes", uptrack: "Not applicable (hosted)", competitor: "Helm chart available" },
    { feature: "GitOps / config in Git", uptrack: "CLI apply/export (YAML)", competitor: "YAML config in Git (built for this)" },
    { feature: "Status page", uptrack: "Included", competitor: "Dashboard doubles as status page, plus badges" },
    { feature: "User management", uptrack: "Multi-user with roles", competitor: "Basic auth or OIDC; no user roles", uptrackWins: true },
  ],

  painPoints: [
    {
      title: "YAML-only configuration",
      description: "Gatus has no web UI for creating or editing monitors. Every endpoint, condition, and alert lives in YAML. This suits GitOps teams, but people who do not edit config files can view the dashboard and cannot change it.",
    },
    {
      title: "You run the infrastructure",
      description: "As with any self-hosted tool, you provision, secure, and update the server or cluster that runs Gatus. If that server goes down, its checks and alerts stop too.",
    },
    {
      title: "No user roles",
      description: "Gatus protects its dashboard with basic auth or OIDC, but it has no user accounts or roles. Giving people different access levels needs outside tooling.",
    },
  ],

  differentiators: [
    "Web UI for everything: create monitors, manage alerts, and view status without editing config files. Anyone on your team can use it.",
    "Nothing to host: no servers, containers, or backups to maintain. Uptrack runs on redundant, multi-node infrastructure.",
    "Alert confirmation with 2-5 consecutive failed checks; several check locations must confirm.",
    "Team members with roles and permissions, included in every paid plan.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you practice GitOps and want monitoring config in version control, Gatus is built for this workflow. Its YAML-first approach is a deliberate design choice.",
    "If you run Kubernetes and want monitoring deployed in your cluster, Gatus has a Helm chart.",
    "If you need SSH, STARTTLS, WebSocket, gRPC, UDP, or SCTP checks, Gatus supports these natively. Uptrack doesn't.",
    "If you want a small single binary you run yourself, Gatus is written in Go and its README describes its footprint as negligibly small.",
    "If your whole team is technical and comfortable with YAML, Gatus gives you precise control over every condition and threshold.",
    "If you want free, open-source monitoring, Gatus is licensed under Apache 2.0.",
  ],
}
